import { type QueryClient, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  fetchRunLogScreenshot,
  getCbcConnection,
  getConfigurationLogin,
  listMigrationItems,
  saveCbcConnection,
  saveConfigurationLogin,
  testCbcConnection,
} from "@/api/config-migration";
import type { CbcConnectionInput, ConfigurationLoginInput } from "@/api/config-migration";

export function useCbcConnection(projectId: string | undefined) {
  return useQuery({
    queryKey: ["cbc-connection", projectId ?? ""],
    queryFn: () => getCbcConnection(projectId as string),
    // `projectId` opcional: chamar incondicionalmente sem guarda disparava
    // uma requisição malformada (`/api/projects//cbc-connection`) em toda
    // tela ainda sem projeto carregado.
    enabled: Boolean(projectId),
  });
}

/**
 * O selo "Configurado para o GROW" depende do login do CBC (testado) e do login da Configuration:
 * o backend o calcula na leitura da conexão da Configuration, então salvar ou testar o CBC também
 * precisa relê-la, senão o selo só muda ao recarregar a tela.
 */
function refreshCbcAndGrowReadiness(queryClient: QueryClient, projectId: string) {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: ["cbc-connection", projectId] }),
    queryClient.invalidateQueries({ queryKey: ["targetConnection", projectId] }),
  ]);
}

export function useSaveCbcConnection(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CbcConnectionInput) =>
      saveCbcConnection(projectId, body),
    onSuccess: () => refreshCbcAndGrowReadiness(queryClient, projectId),
  });
}

/**
 * Testa a conexão com um login real — a api já salva as credenciais antes
 * de testar (um único clique), então a query de conexão também precisa ser
 * invalidada aqui: um teste com sucesso grava `last_login_at`.
 */
export function useTestCbcConnection(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CbcConnectionInput) =>
      testCbcConnection(projectId, body),
    onSuccess: () => refreshCbcAndGrowReadiness(queryClient, projectId),
  });
}

export function useConfigurationLogin(projectId: string | undefined) {
  return useQuery({
    queryKey: ["cbc-configuration-login", projectId ?? ""],
    queryFn: () => getConfigurationLogin(projectId as string),
    enabled: Boolean(projectId),
  });
}

export function useSaveConfigurationLogin(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: ConfigurationLoginInput) => saveConfigurationLogin(projectId, body),
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: ["cbc-configuration-login", projectId] }),
        queryClient.invalidateQueries({ queryKey: ["targetConnection", projectId] }),
      ]),
  });
}

export function useMigrationItems(projectId: string | undefined, { live }: { live: boolean }) {
  return useQuery({
    queryKey: ["migration-items", projectId ?? ""],
    queryFn: () => listMigrationItems(projectId as string),
    // Mesmo motivo do `enabled` em `useCbcConnection`: sem guarda, a tela
    // ainda sem projeto carregado dispararia uma requisição malformada.
    enabled: Boolean(projectId),
    refetchInterval: live ? 30_000 : false,
  });
}

/** Mesma chave e mesma consulta para a miniatura, o modal e o pré-carregamento do terminal. */
export function runLogScreenshotQuery(projectId: string, runId: string, logId: number) {
  return {
    queryKey: ["config-migration-screenshot", projectId, runId, logId] as const,
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      fetchRunLogScreenshot(projectId, runId, logId, signal),
    staleTime: Infinity,
  };
}

/**
 * Print de uma linha do log, baixado quando a miniatura chega perto da tela ou o
 * modal abre (`enabled`).
 *
 * O print nunca muda depois de gravado: `staleTime: Infinity` evita baixar de
 * novo ao reabrir. Nova tentativa segue a política global do cache
 * (`retryUnlessTheServerAlreadyAnswered`): 404 e os demais 4xx de veredito não
 * repetem (408/425/429 repetem uma vez), o resto repete uma vez — o hook não tem
 * `retry` próprio.
 */
export function useRunLogScreenshot(
  projectId: string,
  runId: string,
  logId: number,
  enabled: boolean,
) {
  return useQuery({ ...runLogScreenshotQuery(projectId, runId, logId), enabled });
}
